import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLanguage } from '../../../context/LanguageContext';
import './AdminOrders.css';
import { API_URL } from '../../../app/apiConfig';
import {
  fetchAllOrdersAsync,
  selectOrders,
  selectTotalOrders,
  updateOrderAsync,
} from '../../order/orderSlice';
import {
  PencilIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PhotoIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  KeyIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';

// Helper function to get multilingual text
function getMultilingualText(field, language = 'en') {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    const result = field[language] || field['en'] || field['fr'] || Object.values(field)[0] || '';
    return typeof result === 'string' ? result : '';
  }
  return '';
}

// Helper function to get absolute URL for receipt images
function getReceiptImageUrl(imagePath) {
  if (!imagePath) {
    return '';
  }
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  if (imagePath.startsWith('/uploads/receipts/')) {
    const filename = imagePath.replace('/uploads/receipts/', '');
    return `https://media.subifyhub.com/receipts/${filename}`;
  }
  
  return `${API_URL}${imagePath}`;
}

function AdminOrders() {
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const rawOrders = useSelector(selectOrders);
  const orders = Array.isArray(rawOrders) ? rawOrders : [];
  const totalOrders = useSelector(selectTotalOrders);
  const [editableOrderId, setEditableOrderId] = useState(-1);
  const [sort, setSort] = useState({ _sort: 'createdAt', _order: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [receiptModal, setReceiptModal] = useState({ open: false, order: null });
  const [detailsModal, setDetailsModal] = useState({ open: false, order: null });
  const [copiedId, setCopiedId] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const handleEdit = (order) => {
    setEditableOrderId(editableOrderId === order.id ? -1 : order.id);
  };

  const handleShow = (order) => {
    setDetailsModal({ open: true, order });
  };

  const closeDetailsModal = () => {
    setDetailsModal({ open: false, order: null });
  };

  const handleViewReceipt = (order) => {
    setReceiptModal({ open: true, order });
    setAdminNotes(order.adminNotes || '');
  };

  const closeReceiptModal = () => {
    setReceiptModal({ open: false, order: null });
    setAdminNotes('');
  };

  const copyToClipboard = (text, fieldKey) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const copyOrderId = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleVerifyPayment = async (status) => {
    if (!receiptModal.order) return;
    
    setVerifying(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/api/orders/${receiptModal.order.id}/verify-payment`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ 
          paymentStatus: status,
          adminNotes: adminNotes 
        }),
      });
      
      if (response.ok) {
        dispatch(fetchAllOrdersAsync({ sort, pagination: { _page: page, _limit: pageSize } }));
        closeReceiptModal();
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
    } finally {
      setVerifying(false);
    }
  };

  const handleOrderStatus = (e, order) => {
    const updatedOrder = { ...order, status: e.target.value };
    dispatch(updateOrderAsync(updatedOrder));
    setEditableOrderId(-1);
  };

  const handleOrderPaymentStatus = (e, order) => {
    const updatedOrder = { ...order, paymentStatus: e.target.value };
    dispatch(updateOrderAsync(updatedOrder));
    setEditableOrderId(-1);
  };

  const handleSort = (field) => {
    setSort((prevSort) => {
      const isSameField = prevSort?._sort === field;
      const newOrder = isSameField && prevSort?._order === 'desc' ? 'asc' : 'desc';
      return { _sort: field, _order: newOrder };
    });
    setPage(1);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setPage(1);
  };

  const getPageNumbers = (currentPage, totalPages) => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const chooseColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-purple-100 text-purple-700 border border-purple-200';
      case 'dispatched':
        return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
      case 'delivered':
        return 'bg-green-100 text-green-700 border border-green-200';
      case 'confirmed':
        return 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'verified':
        return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'received':
        return 'bg-green-100 text-green-700 border border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border border-red-200';
      case 'cancelled':
        return 'bg-rose-100 text-rose-700 border border-rose-200';
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  useEffect(() => {
    const pagination = { _page: page, _limit: pageSize };
    dispatch(fetchAllOrdersAsync({ sort, pagination }));
  }, [dispatch, sort, page, pageSize]);

  const totalCount = Number(totalOrders) || orders.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="w-full min-h-[calc(100vh-64px)] flex flex-col bg-white p-0 m-0">
      {/* Header Bar - Full Width 100% Flush */}
      <div className="w-full bg-gradient-to-r from-sky-500 to-sky-600 px-4 sm:px-6 py-2.5 text-white flex items-center justify-between flex-shrink-0 shadow-xs border-b border-sky-600">
        <div className="flex items-center gap-2.5">
          <h1 className="text-base font-bold tracking-tight">Order Management</h1>
          <span className="text-[11px] bg-white/20 px-2.5 py-0.5 rounded-full font-semibold">
            {totalCount} Orders
          </span>
        </div>
        
        <p className="text-sky-100 text-[11px] hidden md:block">
          Manage all orders & payment verifications
        </p>
      </div>

      {/* Table Container - Complete 100% width, flex-1 full height, horizontally scrollable on mobile */}
      <div className="admin-table-scroll overflow-x-auto overflow-y-auto flex-1 w-full min-h-[420px]">
        <table className="admin-orders-table w-full min-w-[1100px] text-[11px]">
          <colgroup>
            <col className="w-[7.5%]" />
            <col className="w-[14.5%]" />
            <col className="w-[11.5%]" />
            <col className="w-[5.5%]" />
            <col className="w-[15%]" />
            <col className="w-[7.5%]" />
            <col className="w-[6%]" />
            <col className="w-[6.5%]" />
            <col className="w-[7.5%]" />
            <col className="w-[6.5%]" />
            <col className="w-[6.5%]" />
            <col className="w-[5.5%]" />
          </colgroup>

          <thead className="sticky top-0 z-20 bg-slate-100 border-b border-slate-200">
            <tr className="text-slate-700 uppercase text-[10px] font-bold leading-normal select-none">
              <th
                className="py-2 px-1.5 text-left cursor-pointer hover:text-sky-600 transition-colors"
                onClick={() => handleSort('id')}
                title="Sort by Order ID"
              >
                <div className="flex items-center gap-0.5 truncate">
                  ID
                  {sort?._sort === 'id' &&
                    (sort?._order === 'asc' ? (
                      <ArrowUpIcon className="w-3 h-3 text-sky-600" />
                    ) : (
                      <ArrowDownIcon className="w-3 h-3 text-sky-600" />
                    ))}
                </div>
              </th>

              <th className="py-2 px-1.5 text-left truncate">Items</th>
              <th className="py-2 px-1.5 text-left truncate">User Email</th>

              <th
                className="py-2 px-1 text-center cursor-pointer hover:text-sky-600 transition-colors"
                onClick={() => handleSort('totalAmount')}
                title="Sort by Amount"
              >
                <div className="flex items-center justify-center gap-0.5 truncate">
                  Amount
                  {sort?._sort === 'totalAmount' &&
                    (sort?._order === 'asc' ? (
                      <ArrowUpIcon className="w-3 h-3 text-sky-600" />
                    ) : (
                      <ArrowDownIcon className="w-3 h-3 text-sky-600" />
                    ))}
                </div>
              </th>

              <th className="py-2 px-1.5 text-left truncate">Address & Credentials</th>
              <th className="py-2 px-1 text-center truncate">O. Status</th>
              <th className="py-2 px-1 text-center truncate">Payment</th>
              <th className="py-2 px-1 text-center truncate">Receipt</th>
              <th className="py-2 px-1 text-center truncate">P. Status</th>

              <th
                className="py-2 px-1 text-center cursor-pointer hover:text-sky-600 transition-colors"
                onClick={() => handleSort('createdAt')}
                title="Sort by Created date"
              >
                <div className="flex items-center justify-center gap-0.5 truncate">
                  Created
                  {sort?._sort === 'createdAt' &&
                    (sort?._order === 'asc' ? (
                      <ArrowUpIcon className="w-3 h-3 text-sky-600" />
                    ) : (
                      <ArrowDownIcon className="w-3 h-3 text-sky-600" />
                    ))}
                </div>
              </th>

              <th
                className="py-2 px-1 text-center cursor-pointer hover:text-sky-600 transition-colors"
                onClick={() => handleSort('updatedAt')}
                title="Sort by Updated date"
              >
                <div className="flex items-center justify-center gap-0.5 truncate">
                  Updated
                  {sort?._sort === 'updatedAt' &&
                    (sort?._order === 'asc' ? (
                      <ArrowUpIcon className="w-3 h-3 text-sky-600" />
                    ) : (
                      <ArrowDownIcon className="w-3 h-3 text-sky-600" />
                    ))}
                </div>
              </th>

              <th className="py-2 px-1 text-center truncate">Actions</th>
            </tr>
          </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700 font-normal">
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="py-8 text-center text-slate-400 italic">
                      No orders found
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-sky-50/60 transition-colors duration-150 h-[40px]"
                    >
                      {/* Order ID */}
                      <td className="py-1.5 px-1.5 text-left">
                        <button
                          type="button"
                          onClick={() => copyOrderId(order.id)}
                          title={`Full ID: ${order.id}\nClick to copy`}
                          className={`font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors ${
                            copiedId === order.id
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
                          }`}
                        >
                          {copiedId === order.id ? 'Copied!' : `#...${order.id.slice(-6)}`}
                        </button>
                      </td>

                      {/* Items */}
                      <td className="py-1.5 px-1.5 text-left">
                        {order.items && order.items.length > 0 ? (
                          <div
                            className="flex items-center gap-1.5"
                            title={order.items
                              .map(
                                (it) =>
                                  `${getMultilingualText(it.product?.title, language)} (Qty: ${it.quantity} × €${it.product?.discountPrice ?? it.product?.price ?? 0})`
                              )
                              .join('\n')}
                          >
                            <img
                              className="w-5 h-5 rounded object-cover border border-slate-200 flex-shrink-0"
                              src={order.items[0].product?.thumbnail || ''}
                              alt=""
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-medium text-slate-800 truncate leading-tight">
                                {getMultilingualText(order.items[0].product?.title, language)}
                              </p>
                              <div className="flex items-center gap-1 text-[10px] text-slate-500 leading-tight">
                                <span>
                                  {order.items[0].quantity} × €{order.items[0].product?.discountPrice ?? order.items[0].product?.price ?? 0}
                                </span>
                                {order.items.length > 1 && (
                                  <span className="bg-sky-100 text-sky-700 px-1 rounded text-[9px] font-semibold">
                                    +{order.items.length - 1}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-[10px] italic">No items</span>
                        )}
                      </td>

                      {/* User Email */}
                      <td className="py-1.5 px-1.5 text-left">
                        <span
                          className="font-medium text-sky-700 truncate block text-[11px]"
                          title={order.user?.email || order.userEmail || order.selectedAddress?.email || 'N/A'}
                        >
                          {order.user?.email || order.userEmail || order.selectedAddress?.email || 'N/A'}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className="py-1.5 px-1 text-center font-bold text-slate-900 text-xs">
                        €{order.totalAmount}
                      </td>

                      {/* Address / Credentials */}
                      <td className="py-1 px-1.5 text-left">
                        {order.selectedAddress ? (
                          <div className="leading-tight">
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-semibold text-slate-800 text-[11px] truncate">
                                {order.selectedAddress.name || 'Customer'}
                              </span>
                              {order.selectedAddress.accountUsername && (
                                <button
                                  type="button"
                                  onClick={() => handleShow(order)}
                                  title="View customer login credentials"
                                  className="flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 transition-colors"
                                >
                                  <KeyIcon className="w-2.5 h-2.5" />
                                  Credentials
                                </button>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-500 truncate block">
                              {order.selectedAddress.contactNumber || order.selectedAddress.email}
                            </span>
                          </div>
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                            Admin Order
                          </span>
                        )}
                      </td>

                      {/* Order Status */}
                      <td className="py-1.5 px-1 text-center">
                        {order.id === editableOrderId ? (
                          <select
                            onChange={(e) => handleOrderStatus(e, order)}
                            defaultValue={order.status}
                            className="px-1 py-0.5 border border-sky-300 rounded bg-white text-slate-700 font-semibold focus:outline-none focus:border-sky-500 text-[10px]"
                          >
                            <option value="pending">Pending</option>
                            <option value="dispatched">Dispatched</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        ) : (
                          <span
                            className={`${chooseColor(
                              order.status
                            )} inline-block py-0.5 px-1.5 rounded-full text-[10px] font-semibold leading-tight`}
                          >
                            {order.status?.substring(0, 3).toUpperCase()}
                          </span>
                        )}
                      </td>

                      {/* Payment Method */}
                      <td className="py-1.5 px-1 text-center">
                        <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-semibold uppercase">
                          {order.paymentMethod?.substring(0, 4) || 'MANU'}
                        </span>
                      </td>

                      {/* Receipt */}
                      <td className="py-1.5 px-1 text-center">
                        {order.receiptImage ? (
                          <button
                            type="button"
                            onClick={() => handleViewReceipt(order)}
                            className="inline-flex items-center justify-center gap-1 px-1.5 py-0.5 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded text-[10px] font-semibold transition-colors mx-auto"
                            title="View uploaded payment receipt"
                          >
                            <PhotoIcon className="w-3 h-3" />
                            View
                          </button>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">None</span>
                        )}
                      </td>

                      {/* Payment Status */}
                      <td className="py-1.5 px-1 text-center">
                        {order.id === editableOrderId ? (
                          <select
                            onChange={(e) => handleOrderPaymentStatus(e, order)}
                            defaultValue={order.paymentStatus}
                            className="px-1 py-0.5 border border-sky-300 rounded bg-white text-slate-700 font-semibold focus:outline-none focus:border-sky-500 text-[10px]"
                          >
                            <option value="pending">Pending</option>
                            <option value="verified">Verified</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        ) : (
                          <span
                            className={`${chooseColor(
                              order.paymentStatus
                            )} inline-block py-0.5 px-1.5 rounded-full text-[10px] font-semibold leading-tight`}
                          >
                            {order.paymentStatus?.substring(0, 3).toUpperCase()}
                          </span>
                        )}
                      </td>

                      {/* Created Date */}
                      <td className="py-1 px-1 text-center leading-tight">
                        <div className="text-[10px] font-medium text-slate-700">
                          {order.createdAt
                            ? new Date(order.createdAt).toLocaleDateString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: '2-digit',
                              })
                            : 'N/A'}
                        </div>
                        <div className="text-[9px] text-slate-400">
                          {order.createdAt
                            ? new Date(order.createdAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : ''}
                        </div>
                      </td>

                      {/* Updated Date */}
                      <td className="py-1 px-1 text-center leading-tight">
                        <div className="text-[10px] font-medium text-slate-700">
                          {order.updatedAt
                            ? new Date(order.updatedAt).toLocaleDateString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: '2-digit',
                              })
                            : 'N/A'}
                        </div>
                        <div className="text-[9px] text-slate-400">
                          {order.updatedAt
                            ? new Date(order.updatedAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : ''}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-1 px-1 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleShow(order)}
                            className="p-1 hover:bg-sky-100 rounded text-slate-600 hover:text-sky-700 transition-colors"
                            title="View all order & credential details"
                          >
                            <EyeIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEdit(order)}
                            className={`p-1 rounded transition-colors ${
                              editableOrderId === order.id
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'hover:bg-sky-100 text-slate-600 hover:text-sky-700'
                            }`}
                            title={editableOrderId === order.id ? 'Exit editing' : 'Edit statuses inline'}
                          >
                            {editableOrderId === order.id ? (
                              <CheckIcon className="w-3.5 h-3.5" />
                            ) : (
                              <PencilIcon className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer - Full Width 100% Flush */}
          <div className="w-full bg-white border-t border-slate-200 px-4 sm:px-6 py-3 flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-xs">
            {/* Left: Range and Total info + Rows per page */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-5 text-xs text-slate-600">
              <div>
                Showing <span className="font-bold text-slate-900">{totalCount === 0 ? 0 : (page - 1) * pageSize + 1}</span> to{' '}
                <span className="font-bold text-slate-900">{Math.min(page * pageSize, totalCount)}</span> of{' '}
                <span className="font-bold text-slate-900">{totalCount}</span> orders
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-slate-500 font-medium">Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="border border-slate-300 rounded-md px-2 py-1 text-xs font-semibold text-slate-800 bg-white hover:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 cursor-pointer transition-colors shadow-2xs"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Right: Navigation Controls */}
            <div className="flex items-center gap-1">
              {/* First Page */}
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={page === 1}
                title="First Page"
                className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-sky-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-colors"
              >
                <ChevronDoubleLeftIcon className="w-4 h-4" />
              </button>

              {/* Previous Page */}
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                title="Previous Page"
                className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-sky-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>

              {/* Desktop Page Numbers */}
              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers(page, totalPages).map((pNum, idx) =>
                  pNum === '...' ? (
                    <span key={`dots-${idx}`} className="px-2 py-1 text-slate-400 text-xs select-none">
                      …
                    </span>
                  ) : (
                    <button
                      key={`page-${pNum}`}
                      type="button"
                      onClick={() => setPage(pNum)}
                      className={`min-w-[30px] h-7 px-2 rounded-md text-xs font-semibold transition-all ${
                        page === pNum
                          ? 'bg-sky-600 text-white shadow-xs'
                          : 'border border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-sky-600'
                      }`}
                    >
                      {pNum}
                    </button>
                  )
                )}
              </div>

              {/* Mobile Page Indicator */}
              <span className="sm:hidden text-xs font-semibold text-slate-700 px-2">
                Page {page} of {totalPages}
              </span>

              {/* Next Page */}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                title="Next Page"
                className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-sky-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>

              {/* Last Page */}
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                title="Last Page"
                className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-sky-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-colors"
              >
                <ChevronDoubleRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

      {/* Full Order Details & Credentials Modal */}
      {detailsModal.open && detailsModal.order && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-sky-500 to-sky-600 px-6 py-4 text-white flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold">Order Details & Credentials</h2>
                <p className="text-sky-100 text-xs">Order #{detailsModal.order.id}</p>
              </div>
              <button
                type="button"
                onClick={closeDetailsModal}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5 text-xs text-slate-700">
              {/* Order Status Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold">Total Amount</span>
                  <p className="text-sm font-bold text-sky-700">€{detailsModal.order.totalAmount}</p>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold">Items Count</span>
                  <p className="text-sm font-semibold text-slate-800">{detailsModal.order.totalItems}</p>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold">Order Status</span>
                  <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${chooseColor(detailsModal.order.status)}`}>
                    {detailsModal.order.status?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-semibold">Payment Status</span>
                  <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${chooseColor(detailsModal.order.paymentStatus)}`}>
                    {detailsModal.order.paymentStatus?.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Customer Provided Credentials (if myaccount) */}
              {detailsModal.order.selectedAddress?.accountUsername && (
                <div className="bg-amber-50/80 border border-amber-200 p-4 rounded-xl space-y-2">
                  <div className="flex items-center gap-1.5 text-amber-900 font-semibold text-xs">
                    <KeyIcon className="w-4 h-4 text-amber-700" />
                    Customer Subscription Credentials
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div className="bg-white p-2.5 rounded-lg border border-amber-200 flex items-center justify-between">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase">Username / Email</span>
                        <p className="font-mono text-xs font-semibold text-slate-800">
                          {detailsModal.order.selectedAddress.accountUsername}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(detailsModal.order.selectedAddress.accountUsername, 'username')}
                        className="p-1 hover:bg-slate-100 rounded text-slate-500"
                        title="Copy username"
                      >
                        {copiedField === 'username' ? (
                          <CheckIcon className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>

                    <div className="bg-white p-2.5 rounded-lg border border-amber-200 flex items-center justify-between">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase">Password</span>
                        <p className="font-mono text-xs font-semibold text-slate-800">
                          {detailsModal.order.selectedAddress.accountPassword}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(detailsModal.order.selectedAddress.accountPassword, 'password')}
                        className="p-1 hover:bg-slate-100 rounded text-slate-500"
                        title="Copy password"
                      >
                        {copiedField === 'password' ? (
                          <CheckIcon className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Contact & Address */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                <h3 className="font-semibold text-slate-800 text-xs">Customer Information</h3>
                {detailsModal.order.selectedAddress ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-400 text-[10px]">Full Name:</span>{' '}
                      <span className="font-medium text-slate-800">{detailsModal.order.selectedAddress.name}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-[10px]">Email:</span>{' '}
                      <span className="font-medium text-slate-800">{detailsModal.order.selectedAddress.email}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-[10px]">Contact Number:</span>{' '}
                      <span className="font-medium text-slate-800">{detailsModal.order.selectedAddress.contactNumber}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-[10px]">Subscription Mode:</span>{' '}
                      <span className="font-medium text-slate-800 capitalize">
                        {detailsModal.order.selectedAddress.subscriptionType || detailsModal.order.subscriptionType || 'N/A'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 italic">Admin created order (no customer address registered)</p>
                )}
              </div>

              {/* Order Items List */}
              <div className="space-y-2">
                <h3 className="font-semibold text-slate-800 text-xs">Purchased Items</h3>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                  {detailsModal.order.items?.map((item, idx) => (
                    <div key={idx} className="p-3 bg-white flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={item.product?.thumbnail || ''}
                          alt=""
                          className="w-8 h-8 rounded object-cover border border-slate-200 flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate text-xs">
                            {getMultilingualText(item.product?.title, language)}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {item.planType === 'yearly' ? 'Yearly' : 'Monthly'} Subscription
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-slate-900 text-xs">
                          €{item.product?.discountPrice ?? item.product?.price ?? 0}
                        </p>
                        <p className="text-[10px] text-slate-400">Qty: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 flex justify-between items-center flex-shrink-0">
              {detailsModal.order.receiptImage ? (
                <button
                  type="button"
                  onClick={() => {
                    const ord = detailsModal.order;
                    closeDetailsModal();
                    handleViewReceipt(ord);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200 rounded-lg text-xs font-semibold"
                >
                  <PhotoIcon className="w-4 h-4" />
                  View Payment Receipt
                </button>
              ) : (
                <div />
              )}
              <button
                type="button"
                onClick={closeDetailsModal}
                className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-lg text-xs transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Receipt Modal */}
      {receiptModal.open && receiptModal.order && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-sky-500 to-sky-600 px-6 py-4 text-white flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold">Payment Receipt</h2>
                <p className="text-sky-100 text-xs">Order #{receiptModal.order.id}</p>
              </div>
              <button
                type="button"
                onClick={closeReceiptModal}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 max-h-[calc(90vh-140px)] text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase">Amount</span>
                  <p className="font-bold text-sky-700 text-sm">€{receiptModal.order.totalAmount}</p>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase">Customer</span>
                  <p className="font-semibold text-slate-800 truncate">
                    {receiptModal.order.selectedAddress?.name || receiptModal.order.user?.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase">Payment Method</span>
                  <p className="font-semibold text-slate-800 capitalize">{receiptModal.order.paymentMethod || 'Manual'}</p>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase">Status</span>
                  <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${chooseColor(receiptModal.order.paymentStatus)}`}>
                    {receiptModal.order.paymentStatus?.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Receipt Image Display */}
              <div className="border border-slate-200 rounded-xl p-3 bg-slate-50 flex flex-col items-center">
                <img
                  src={getReceiptImageUrl(receiptModal.order.receiptImage)}
                  alt="Payment Receipt"
                  className="max-w-full h-auto rounded-lg shadow-sm"
                  style={{ maxHeight: '350px', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"%3E%3Cpath stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /%3E%3C/svg%3E';
                  }}
                />
                {receiptModal.order.receiptUploadedAt && (
                  <p className="text-[10px] text-slate-400 mt-2">
                    Uploaded on {new Date(receiptModal.order.receiptUploadedAt).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Admin Notes */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-xs">
                  Admin Verification Notes
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Notes for this receipt or customer..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 outline-none text-xs"
                  rows={2}
                />
              </div>
            </div>

            <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 flex gap-2 justify-end flex-shrink-0">
              {receiptModal.order.paymentStatus !== 'verified' && (
                <button
                  type="button"
                  onClick={() => handleVerifyPayment('verified')}
                  disabled={verifying}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg text-xs transition-colors disabled:opacity-50"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  {verifying ? 'Saving...' : 'Verify Payment'}
                </button>
              )}
              {receiptModal.order.paymentStatus !== 'rejected' && (
                <button
                  type="button"
                  onClick={() => handleVerifyPayment('rejected')}
                  disabled={verifying}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg text-xs transition-colors disabled:opacity-50"
                >
                  <XCircleIcon className="w-4 h-4" />
                  {verifying ? 'Saving...' : 'Reject Payment'}
                </button>
              )}
              <button
                type="button"
                onClick={closeReceiptModal}
                className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-lg text-xs transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;
